import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import LockedPage from '../components/LockedPage';

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatPrice(p) {
  if (!p && p !== 0) return null;
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(p);
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();
}

// ── Sub-components ────────────────────────────────────────────────────────────

function Chip({ children, color = 'gray' }) {
  const cls = {
    gray:   'bg-gray-100 text-gray-600',
    blue:   'bg-blue-50 text-blue-700',
    green:  'bg-green-50 text-green-700',
    amber:  'bg-amber-50 text-amber-700',
    red:    'bg-red-50 text-red-600',
    indigo: 'bg-indigo-50 text-indigo-700',
  }[color] ?? 'bg-gray-100 text-gray-600';
  return <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{children}</span>;
}

function SummaryBlock({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className="text-sm text-gray-700 leading-relaxed">{value}</p>
    </div>
  );
}

function BookingRow({ booking, label }) {
  if (!booking) return null;
  const names = Array.isArray(booking.traveler_names)
    ? booking.traveler_names.join(' & ')
    : (booking.traveler_names || '—');
  return (
    <div className="flex items-start gap-3 py-2.5 border-b border-gray-50 last:border-0">
      <div className="flex-shrink-0 mt-0.5">
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-semibold ${
          label === 'Volgende' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
        }`}>{label}</span>
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-800 truncate">
          {booking.destination || '—'}
          {booking.hotel_name && <span className="font-normal text-gray-500"> · {booking.hotel_name}</span>}
        </p>
        <p className="text-xs text-gray-500 mt-0.5">
          {formatDate(booking.departure_date)}
          {booking.return_date && ` – ${formatDate(booking.return_date)}`}
          {booking.total_price && <span className="ml-2 font-medium text-gray-700">{formatPrice(booking.total_price)}</span>}
        </p>
      </div>
    </div>
  );
}

function IntelligenceToggle({ clientId, enabled, onChange }) {
  const [loading, setLoading] = useState(false);
  const { authFetch } = useAuth();

  const toggle = async (e) => {
    e.stopPropagation();
    setLoading(true);
    try {
      const res = await authFetch(`/api/clients/${clientId}/intelligence`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !enabled }),
      });
      if (res.ok) onChange(!enabled);
    } catch (_) {}
    setLoading(false);
  };

  return (
    <button
      onClick={toggle}
      disabled={loading}
      title={enabled ? 'AI-inzichten actief · klik om uit te zetten' : 'AI-inzichten uit · klik om aan te zetten'}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none disabled:opacity-50 ${
        enabled ? 'bg-indigo-500' : 'bg-gray-300'
      }`}
    >
      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
        enabled ? 'translate-x-4' : 'translate-x-0.5'
      }`} />
    </button>
  );
}

function InsightPanel({ client, onIntelligenceChange, onInsightsGenerated }) {
  const { authFetch } = useAuth();
  const [generating, setGenerating] = useState(false);
  const ins = client.client_insights;

  const hasInsights = ins && (
    ins.tone_summary || ins.relationship_summary || ins.travel_style_summary ||
    ins.soft_preferences?.length || ins.soft_avoidances?.length
  );

  async function handleGenerate() {
    setGenerating(true);
    try {
      await authFetch(`/api/clients/${client.id}/generate-insights`, { method: 'POST' });
      onInsightsGenerated(client.id);
    } catch (_) {}
    setGenerating(false);
  }

  const hasBookings = client.all_bookings?.length > 0 || client.last_booking || client.next_bookings?.length > 0;

  return (
    <div className="border-t border-gray-100 bg-gray-50/60">
      <div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">

        {/* ── AI-inzichten ── */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">AI-inzichten</p>
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{ins?.intelligence_enabled !== false ? 'Actief' : 'Uitgeschakeld'}</span>
              <IntelligenceToggle
                clientId={client.id}
                enabled={client.intelligence_enabled}
                onChange={v => onIntelligenceChange(client.id, v)}
              />
            </div>
          </div>

          {hasInsights ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <SummaryBlock label="Communicatietoon"  value={ins.tone_summary} />
              <SummaryBlock label="Klantrelatie"      value={ins.relationship_summary} />
              <SummaryBlock label="Reistijl"          value={ins.travel_style_summary} />
            </div>
          ) : (
            <div className="flex items-center gap-3">
              <p className="text-xs text-gray-400 italic flex-1">
                {client.all_bookings?.length > 0
                  ? 'Nog geen inzichten gegenereerd.'
                  : 'Inzichten worden opgebouwd zodra er boekingen zijn.'}
              </p>
              {client.all_bookings?.length > 0 && (
                <button
                  onClick={handleGenerate}
                  disabled={generating}
                  className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg text-xs font-medium transition-colors"
                >
                  {generating ? (
                    <>
                      <svg className="w-3 h-3 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                      </svg>
                      Genereren…
                    </>
                  ) : (
                    <>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z"/>
                      </svg>
                      Genereer inzichten
                    </>
                  )}
                </button>
              )}
            </div>
          )}

          {/* Preferences / Avoidances */}
          {(ins?.soft_preferences?.length > 0 || ins?.soft_avoidances?.length > 0) && (
            <div className="space-y-2">
              {ins.soft_preferences?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Voorkeuren</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ins.soft_preferences.map((p, i) => <Chip key={i} color="green">{p}</Chip>)}
                  </div>
                </div>
              )}
              {ins.soft_avoidances?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Vermijdt</p>
                  <div className="flex flex-wrap gap-1.5">
                    {ins.soft_avoidances.map((a, i) => <Chip key={i} color="red">{a}</Chip>)}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Stats */}
          {ins && (
            <div className="flex gap-4 pt-1">
              {[
                { label: 'Boekingen',    value: ins.booking_count },
                { label: 'Voorstellen',  value: ins.proposal_count },
                { label: 'FAQ',          value: ins.faq_count },
              ].filter(s => s.value != null).map(s => (
                <div key={s.label} className="text-center">
                  <p className="text-base font-bold text-gray-800">{s.value}</p>
                  <p className="text-xs text-gray-400">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Boekingen ── */}
        <div>
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Boekingen</p>
          {hasBookings ? (
            <div>
              {client.next_bookings?.map((b, i) => (
                <BookingRow key={b.id} booking={b} label={i === 0 ? 'Volgende' : 'Gepland'} />
              ))}
              {client.last_booking && <BookingRow booking={client.last_booking} label={client.last_booking.departure_date ? 'Laatste' : 'Boeking'} />}
              {/* Remaining undated bookings not already shown as last_booking */}
              {client.all_bookings?.filter(b => !b.departure_date && b.id !== client.last_booking?.id).map(b => (
                <BookingRow key={b.id} booking={b} label="Boeking" />
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic">Geen boekingen gevonden</p>
          )}

          {/* Emails */}
          {client.client_emails?.length > 0 && (
            <div className="mt-4">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">E-mailadressen</p>
              <div className="space-y-1">
                {client.client_emails.filter(e => e.is_active).map(e => (
                  <div key={e.id} className="flex items-center gap-1.5">
                    <span className="text-sm text-gray-700 truncate">{e.email}</span>
                    {e.is_primary && <Chip color="blue">Primair</Chip>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

function KlantinzichtenInner() {
  const { authFetch } = useAuth();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const load = useCallback(async () => {
    try {
      const res  = await authFetch('/api/clients');
      const data = await res.json();
      if (res.ok) setClients(Array.isArray(data) ? data : []);
    } catch (_) {}
    setLoading(false);
  }, [authFetch]);

  useEffect(() => { load(); }, [load]);

  const handleIntelligenceChange = useCallback((id, enabled) => {
    setClients(prev => prev.map(c => c.id === id ? { ...c, intelligence_enabled: enabled } : c));
  }, []);

  const handleInsightsGenerated = useCallback(() => {
    load(); // reload all clients to get fresh insights
  }, [load]);

  const handleDelete = useCallback(async (e, id) => {
    e.stopPropagation();
    if (!window.confirm('Klant en alle gekoppelde inzichten verwijderen?')) return;
    setDeletingId(id);
    try {
      const res = await authFetch(`/api/clients/${id}`, { method: 'DELETE' });
      if (res.ok) {
        setClients(prev => prev.filter(c => c.id !== id));
        setExpanded(prev => prev === id ? null : prev);
      }
    } catch (_) {}
    setDeletingId(null);
  }, [authFetch]);

  const filtered = clients.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      c.full_name?.toLowerCase().includes(q) ||
      c.primary_email?.toLowerCase().includes(q) ||
      c.client_emails?.some(e => e.email?.toLowerCase().includes(q))
    );
  });

  return (
    <div className="page-enter">
      {/* Header */}
      <div className="pro-page-header">
        <h1 className="text-xl font-semibold text-gray-900">Klantinzichten</h1>
        <p className="text-sm text-gray-500 mt-0.5">Overzicht van uw klanten en AI-inzichten per klant</p>
      </div>

      <div className="p-8">

        {/* Stats */}
        {!loading && clients.length > 0 && (
          <div className="grid grid-cols-3 gap-4 mb-6">
            {[
              { label: 'Totaal klanten',        value: clients.length },
              { label: 'Met AI-inzichten',       value: clients.filter(c => c.client_insights?.tone_summary || c.client_insights?.travel_style_summary).length },
              { label: 'Intelligence actief',    value: clients.filter(c => c.intelligence_enabled).length },
            ].map(({ label, value }) => (
              <div key={label} className="pro-stat-card">
                <p className="text-xl font-bold text-gray-900">{value}</p>
                <p className="text-xs text-gray-500">{label}</p>
              </div>
            ))}
          </div>
        )}

        {/* Client list */}
        <div className="pro-card overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-gray-800">Klanten</h2>
              {clients.length > 0 && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded text-xs font-medium">{clients.length}</span>
              )}
            </div>
            {clients.length > 0 && (
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Zoek op naam of e-mail…"
                className="pl-4 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-200 w-64"
              />
            )}
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-16">
              <svg className="w-8 h-8 text-blue-400 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <svg className="w-8 h-8 text-gray-300 mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="text-gray-600 font-medium text-sm">
                {search ? 'Geen klanten gevonden' : 'Nog geen klanten'}
              </p>
              <p className="text-gray-400 text-xs mt-1">
                {search ? 'Pas je zoekopdracht aan' : 'Klanten worden aangemaakt vanuit de boekingsstroom'}
              </p>
            </div>
          ) : (
            <div>
              {filtered.map(client => (
                <div key={client.id} className="border-b border-gray-50 last:border-0">
                  {/* Summary row */}
                  <div
                    onClick={() => setExpanded(prev => prev === client.id ? null : client.id)}
                    className={`flex items-center gap-2 px-5 py-3.5 cursor-pointer hover:bg-blue-50/30 transition-colors ${
                      expanded === client.id ? 'bg-blue-50/20' : ''
                    }`}
                  >
                    {/* Chevron */}
                    <svg
                      className={`w-4 h-4 text-gray-300 flex-shrink-0 transition-transform ${expanded === client.id ? 'rotate-90' : ''}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>

                    {/* Avatar */}
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-xs font-bold text-indigo-700 flex-shrink-0">
                      {initials(client.full_name)}
                    </div>

                    {/* Name + email */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-800 truncate">{client.full_name}</span>
                        {!client.intelligence_enabled && (
                          <Chip color="gray">Intelligence uit</Chip>
                        )}
                        {client.intelligence_enabled && client.client_insights?.tone_summary && (
                          <Chip color="indigo">AI-inzichten</Chip>
                        )}
                      </div>
                      {client.primary_email && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{client.primary_email}</p>
                      )}
                    </div>

                    {/* Tone summary preview */}
                    {client.client_insights?.tone_summary && (
                      <p className="hidden md:block text-xs text-gray-500 max-w-xs truncate flex-shrink-0">
                        {client.client_insights.tone_summary}
                      </p>
                    )}

                    {/* Booking count */}
                    <div className="hidden sm:flex items-center gap-1 flex-shrink-0 text-xs text-gray-400">
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      {(client.client_insights?.booking_count ?? 0)}
                    </div>

                    {/* Source */}
                    {client.source_first_seen && (
                      <Chip color="gray">{client.source_first_seen}</Chip>
                    )}

                    {/* Last seen */}
                    <span className="hidden lg:block text-xs text-gray-400 flex-shrink-0">
                      {formatDate(client.last_seen_at)}
                    </span>

                    {/* Delete */}
                    <button
                      onClick={e => handleDelete(e, client.id)}
                      disabled={deletingId === client.id}
                      title="Klant verwijderen"
                      className="flex-shrink-0 p-1 rounded text-gray-300 hover:text-red-400 hover:bg-red-50 transition-colors disabled:opacity-40"
                    >
                      {deletingId === client.id ? (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                        </svg>
                      ) : (
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      )}
                    </button>
                  </div>

                  {/* Expanded insight panel */}
                  {expanded === client.id && (
                    <InsightPanel
                      client={client}
                      onIntelligenceChange={handleIntelligenceChange}
                      onInsightsGenerated={handleInsightsGenerated}
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Klantinzichten() {
  const { hasAccess } = useSubscription();
  if (!hasAccess('bureau')) return <LockedPage featureName="Klantinzichten"><KlantinzichtenInner /></LockedPage>;
  return <KlantinzichtenInner />;
}
